/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useLayoutEffect, useState } from 'react';

import isMobileApp from 'js/lib/isMobileApp';

import { Button, useMediaQuery } from '@coursera/cds-core';

import {
  DEFAULT_PAGE_HEADER_HEIGHT,
  MOBILE_HEADER_BREAKPOINT,
  MOBILE_HEADER_BREAKPOINT_MEDIA_QUERY,
  PAGE_HEADER_Z_INDEX,
} from 'bundles/composable-page-header/constants';
import { usePageHeaderHeight } from 'bundles/composable-page-header/contexts/PageHeaderHeightContext';
import { usePageHeaderOffset } from 'bundles/composable-page-header/contexts/PageHeaderOffsetContext';
import useIsMainContentContainerAvailable from 'bundles/composable-page-header/hooks/useIsMainContentContainerAvailable';
import useScrollInfo from 'bundles/composable-page-header/hooks/useScrollInfo';
import useUserAgent from 'bundles/page/hooks/useUserAgent';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  desktopMainNav: React.ReactNode;
  mobileMainNav: React.ReactNode;
  // A list of banner components that this page header may display. Name is used for key.
  banners?: Array<{
    name: string;
    component: React.ReactNode;
  }>;
  // Additional navigation that resides above the main nav that displays a list of high level links.
  metaNav?: React.ReactNode;
  // Mark as true if the page header should not be fixed position and instead be a static element that stays at the top of the page document,
  // instead of being fixed to the window.
  isPositionStatic?: boolean;
  // Activates functionality that moves/hides the page header on scroll down and moves/shows the page header on scroll up.
  // Configurable for both mobile and desktop headers. This is not compatible with isPositionStatic.
  scrollMovementOffset?: {
    desktop?: number;
    mobile?: number;
  };
};

const styles = {
  header: css`
    position: sticky;
    top: 0;
    left: 0;
    width: 100%;
    transition: transform 0.2s ease-in-out;
    z-index: ${PAGE_HEADER_Z_INDEX};
    background-color: var(--cds-color-neutral-background-primary);
  `,
  positionIsStatic: css`
    position: static;
  `,
  navigationBarsContainer: css`
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  desktopNav: css`
    display: block;
    position: relative;

    @media (max-width: ${MOBILE_HEADER_BREAKPOINT}px) {
      display: none;
    }
  `,
  mobileNav: css`
    display: none;
    position: relative;

    @media (max-width: ${MOBILE_HEADER_BREAKPOINT}px) {
      display: block;
    }
  `,
  // Some legacy elements rely on inherited styling added in legacy page header.
  globalHeaderStyles: css`
    font-family: 'Source Sans Pro', Arial, sans-serif;
  `,
  skipToMainContent: (showButton: boolean) => {
    if (!showButton) {
      return css`
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        border: 0;
      `;
    } else {
      return css`
        position: absolute;
        top: 10px;
        left: 10px;
        z-index: 9999;
      `;
    }
  },
};

const getOffsetStyles = (offsetHeight: number) => ({
  transform: `translateY(-${offsetHeight}px)`,
});

const GlobalPageHeaderContainer = React.forwardRef<HTMLDivElement, Props>(
  ({ desktopMainNav, mobileMainNav, banners = [], metaNav, isPositionStatic, scrollMovementOffset }, ref) => {
    const [shouldDisplaySkipToMainContentButton, setShouldDisplaySkipToMainContentButton] = useState(false);
    const pageHeaderHeightInfo = usePageHeaderHeight();
    const pageHeaderOffsetInfo = usePageHeaderOffset();

    const isMainContentContainerAvailable = useIsMainContentContainerAvailable();
    const userAgent = useUserAgent();

    const isMobileWeb = useMediaQuery(MOBILE_HEADER_BREAKPOINT_MEDIA_QUERY, {
      defaultMatches: userAgent?.isMobileBrowser || userAgent?.isMobile,
    });

    useLayoutEffect(() => {
      if (!isPositionStatic && scrollMovementOffset !== undefined) {
        if (isMobileWeb && scrollMovementOffset?.mobile !== undefined) {
          pageHeaderHeightInfo?.setHeight(DEFAULT_PAGE_HEADER_HEIGHT + scrollMovementOffset.mobile);
          pageHeaderOffsetInfo?.setScrollMovementOffset(scrollMovementOffset.mobile);
        } else if (!isMobileWeb && scrollMovementOffset?.desktop !== undefined) {
          pageHeaderHeightInfo?.setHeight(DEFAULT_PAGE_HEADER_HEIGHT + scrollMovementOffset.desktop);
          pageHeaderOffsetInfo?.setScrollMovementOffset(scrollMovementOffset.desktop);
        }
      }
    }, [isMobileWeb, isPositionStatic, scrollMovementOffset, pageHeaderHeightInfo, pageHeaderOffsetInfo]);

    const { isScrollingDown, lastScrollPosition } = useScrollInfo({
      delta: pageHeaderOffsetInfo?.scrollMovementOffset ?? Number.MAX_VALUE,
    });

    useLayoutEffect(() => {
      if (!isPositionStatic && scrollMovementOffset !== undefined && pageHeaderOffsetInfo?.scrollMovementOffset) {
        const isHeaderOffset =
          !isPositionStatic &&
          lastScrollPosition >= (pageHeaderHeightInfo?.height ?? DEFAULT_PAGE_HEADER_HEIGHT) &&
          isScrollingDown;
        pageHeaderOffsetInfo?.setShouldOffsetHeader(isHeaderOffset);
      }
    }, [
      isScrollingDown,
      lastScrollPosition,
      isPositionStatic,
      scrollMovementOffset,
      pageHeaderHeightInfo?.height,
      pageHeaderOffsetInfo,
    ]);

    // We don't want to show the header if a page is being opened in a mobile web view. Mobile apps have their own header.
    if (isMobileApp.get()) {
      return null;
    }

    const offsetStyles = pageHeaderOffsetInfo?.shouldOffsetHeader
      ? getOffsetStyles(pageHeaderOffsetInfo.scrollMovementOffset)
      : undefined;

    return (
      <div
        ref={ref}
        className="rc-GlobalPageHeaderContainer"
        css={[styles.globalHeaderStyles, styles.header, isPositionStatic && styles.positionIsStatic]}
        style={offsetStyles}
      >
        {isMainContentContainerAvailable && (
          <Button
            component="a"
            href="#main"
            css={styles.skipToMainContent(shouldDisplaySkipToMainContentButton)}
            onFocus={() => setShouldDisplaySkipToMainContentButton(true)}
            onBlur={() => setShouldDisplaySkipToMainContentButton(false)}
          >
            {_t('Skip to main content')}
          </Button>
        )}
        <nav
          css={styles.navigationBarsContainer}
          data-testid="page-header-wrapper"
          // Need this to pass e2e tests
          data-e2e="page-header"
        >
          {metaNav}
          <div css={styles.desktopNav}>{desktopMainNav}</div>
          <div css={styles.mobileNav}>{mobileMainNav}</div>
        </nav>
        {banners.map((banner) => (
          <React.Fragment key={banner.name}>{banner.component}</React.Fragment>
        ))}
      </div>
    );
  }
);

export default GlobalPageHeaderContainer;
